import { useLazyQuery, useMutation } from '@apollo/client';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { GET_CANDIDATE_RESUMES } from '../../candidate/gql';
import { REQUEST_AI_SCORE } from '../gql';

const SCORE_COLOR = (score: number) =>
  score >= 7 ? '#28a745' : score >= 4 ? '#eaa827' : '#dc3545';

const SCORE_LABEL = (score: number) =>
  score >= 7 ? 'Strong Match' : score >= 4 ? 'Moderate Match' : 'Weak Match';

interface Resume {
  id: string;
  resumeFileName: string;
  createdAt: string;
}

interface Props {
  isOpen: boolean;
  toggle: () => void;
  jobApplicantId: string;
  candidateId: string;
  aiScore: number;
  aiScoreReason: string;
  aiScoredResumeId?: string;
  onRescored: (data: { aiScore: number; aiScoreReason: string; aiScoredResumeId: string; aiScoredAt: string }) => void;
}

const AiScoreModal: FC<Props> = ({
  isOpen,
  toggle,
  jobApplicantId,
  candidateId,
  aiScore,
  aiScoreReason,
  aiScoredResumeId,
  onRescored,
}) => {
  const [selectedResumeId, setSelectedResumeId] = useState(aiScoredResumeId ?? '');
  const [resumes, setResumes] = useState<Resume[]>([]);

  const [fetchResumes, { loading: loadingResumes }] = useLazyQuery(GET_CANDIDATE_RESUMES, {
    fetchPolicy: 'cache-first',
    onCompleted: (data) => {
      const list: Resume[] = data?.getCandidateResumes ?? [];
      setResumes(list);
      if (!selectedResumeId && list.length) setSelectedResumeId(list[0].id);
    },
  });

  const [requestScore, { loading: scoring }] = useMutation(REQUEST_AI_SCORE, {
    onCompleted: (data) => {
      const result = data?.requestAiScore;
      if (result) {
        onRescored(result);
        toast.success('Re-score complete');
      }
    },
    onError: (err) => toast.error(err.message),
  });

  useEffect(() => {
    if (isOpen && candidateId) {
      fetchResumes({ variables: { candidateId } });
    }
  }, [isOpen, candidateId]);

  const color = SCORE_COLOR(aiScore);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle}>AI Match Score</ModalHeader>
      <ModalBody>
        {/* Score display */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 22,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {aiScore}/10
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color }}>{SCORE_LABEL(aiScore)}</div>
          </div>
        </div>

        {/* Reason */}
        <p style={{ lineHeight: 1.6, color: '#343a40', marginBottom: 24 }}>{aiScoreReason}</p>

        {/* Resume selector */}
        <div>
          <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>
            Re-score with a different resume:
          </label>
          {loadingResumes ? (
            <Spinner size="sm" />
          ) : (
            <Input
              type="select"
              value={selectedResumeId}
              onChange={(e) => setSelectedResumeId(e.target.value)}
            >
              {resumes.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.resumeFileName || `Resume (${new Date(r.createdAt).toLocaleDateString()})`}
                </option>
              ))}
            </Input>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          disabled={scoring || !selectedResumeId}
          onClick={() =>
            requestScore({ variables: { jobApplicantId, resumeId: selectedResumeId } })
          }
        >
          {scoring ? <><Spinner size="sm" className="mr-2" /> Scoring...</> : 'Re-score'}
        </Button>
        <Button color="secondary" onClick={toggle}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};

export default AiScoreModal;
